import { ZButton } from "@/shared/components/button";
import { ZInput } from "@/shared/components/input";
import {
  ZModal,
  ZModalBody,
  ZModalContent,
  ZModalFooter,
  ZModalHeader,
} from "@/shared/components/modal";
import VendorSearch from "@/shared/components/VendorSearchAutocomplete";
import type { AxiosError } from "axios";
import type { ApiErrorResponse } from "@/shared/utils/api-error-handler";
import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";

interface CreateFormModalProps {
  isOpen: boolean;
  onSubmit: (vendorName: string, productName: string | null) => Promise<void>;
}

const CreateFormModal: React.FC<CreateFormModalProps> = ({
  isOpen,
  onSubmit,
}) => {
  const [vendorName, setVendorName] = useState("");
  const [productName, setProductName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleVendorSelect = useCallback(
    (vendor: { name: string; domain?: string; logo_url?: string }) => {
      setVendorName(vendor.name);
      if (error) setError("");
    },
    [error],
  );

  const handleVendorInputChange = useCallback(
    (value: string) => {
      setVendorName(value.trimStart());
      if (error) setError("");
    },
    [error],
  );

  const handleProceed = async () => {
    if (!vendorName.trim()) {
      setError("Third-Party name is required");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await onSubmit(vendorName.trim(), productName.trim() || null);
    } catch (error) {
      const axiosError = error as AxiosError<ApiErrorResponse>;
      const apiMessage = axiosError?.response?.data?.error_message;
      setError(
        typeof apiMessage === "string" && apiMessage.trim()
          ? apiMessage
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ZModal isOpen={isOpen} hideCloseButton isDismissable={false}>
      <ZModalContent>
        <ZModalHeader className="flex flex-col gap-1">
          <h3 className="text-lg font-semibold">
            Request a Third-Party Risk Assessment
          </h3>
          <p className="text-sm text-gray-500 font-normal">
            Enter the details of the third-party company and product you need
            the security team to review.
          </p>
        </ZModalHeader>
        <ZModalBody className="flex flex-col gap-4">
          <VendorSearch
            label="Third-Party"
            isRequired
            placeholder="Enter third-party name"
            value={vendorName}
            onSelectionChange={handleVendorSelect}
            onInputChange={handleVendorInputChange}
            errorMessage={error}
            isInvalid={!!error}
          />
          <ZInput
            label="Product Name (Optional)"
            placeholder="Enter product name (optional)"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            variant="faded"
          />
        </ZModalBody>
        <ZModalFooter>
          <ZButton
            className="w-full"
            onClick={() => void handleProceed()}
            isDisabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Please Wait
              </>
            ) : (
              "Proceed"
            )}
          </ZButton>
        </ZModalFooter>
      </ZModalContent>
    </ZModal>
  );
};

export default CreateFormModal;
